// The charge glossary: which section of a supplier rate form a charge belongs to, Origin, Freight
// or Destination. Every form the rate-parsing workflow drafts gets its sections from here when it
// is drafted, before review: by a converter script (the file check, fileChecks.js) or by the
// agent (buildForm in agent.js), like the forms CargoFive imports produce. The reviewer can move a
// charge to another section; publish saves the form as reviewed. Readable version and how to update
// it: docs/air-agent/charge-glossary.md (generated from this file:
// node docs/air-agent/build-charge-glossary.mjs). Agents' rule: R11 in rules-shared.md.
//
// How a charge is placed, first match wins:
//   0. The side the rate file itself gives the charge (a glossary sheet's "Origin Locals"), passed
//      in as `stated`: the carrier's own word about its own charge wins over everything below;
//   0b. its name in Monada's own list (MONADA below): charges placed by hand, from carriers' files
//      that state them (Swire's "Origin Locals" / "Destination Locals");
//   1. Its name in CargoFive's own classification, when CargoFive puts it in one section at least
//      90% of the time (CARGOFIVE below);
//   2. the carrier's own transport and security (feeder, pre/on-carriage, haulage, vessel or carrier
//      security) is Freight, as CargoFive files it ("Transport Additional Origin": freight);
//   3. a side named in the charge itself: "origin", "export", "OTHC"… or "destination", "import",
//      "DTHC", "delivery order"…;
//   4. CargoFive's majority section for a name it splits (BL fee: origin 77%, destination 23%);
//   5. what kind of charge it is (terminal handling, seals, VGM: origin; bunker, fuel, emissions,
//      manifests, dangerous goods, feeder: freight);
//   6. otherwise it is not in the glossary: the rate-parsing workflow takes the section the file
//      itself gives, or asks Claude, and remembers the answer (rateAgent/chargeSections.js), passed
//      in here as `learned`. Freight only when none of that answers.
// Text fields (Carrier, Contract No., Remarks…) go at the top of Freight.

// From 271 CargoFive payloads in the rate-import training store (137,189 charges, 2026-10-09):
// normalised charge name → [section (o / f / d), share of its charges in that section, count].
// CargoFive's freight and freight_surcharge are both Freight here.
const CARGOFIVE = {
    "OCEAN FREIGHT": ["f", 1.0, 51502],
    "BL FEE": ["o", 0.77, 9775],
    "EU ETS": ["f", 1.0, 8447],
    "TRANSPORT ADDITIONAL DESTINATION": ["f", 1.0, 6229],
    "SERVICE FEE": ["o", 0.84, 5526],
    "EMERGENCY BUNKER": ["f", 1.0, 5394],
    "ADVANCE MANIFEST SUBMISSION": ["f", 0.8, 4401],
    "IMO SOX COMPLIANCE CHARGE": ["f", 1.0, 3528],
    "TERMINAL HANDLING ORIGIN": ["o", 1.0, 3391],
    "TRANSPORT ADDITIONAL ORIGIN": ["f", 0.95, 3125],
    "PORT SERVICE": ["o", 0.6, 2605],
    "EMERGENCY FUEL": ["f", 0.99, 1961],
    "TERMINAL HANDLING DESTINATION": ["d", 1.0, 1934],
    "TERMINAL HANDLING CHARGE": ["o", 0.79, 1425],
    "DANGEROUS CARGO": ["f", 0.99, 1414],
    "EMISSION FEE": ["f", 1.0, 1271],
    "GENERAL RATE INCREASE": ["f", 1.0, 1176],
    "CONTAINER PROTECT ESSENTIAL": ["d", 1.0, 1160],
    "ENVIRONMENTAL FUEL FEE": ["f", 1.0, 1151],
    "PORT CHARGES": ["d", 0.72, 1147],
    "BUNKER": ["f", 1.0, 1057],
    "ORIGIN INTERNATIONAL SHIP AND PORT SECURITY": ["o", 0.99, 1036],
    "PEAK SEASON": ["f", 1.0, 990],
    "ENTRY SUMMARY DECLARATION": ["f", 0.96, 963],
    "VERIFIED GROSS MASS": ["o", 0.97, 929],
    "HANDLING CHARGE": ["d", 0.98, 833],
    "DESTINATION INTERNATIONAL SHIP AND PORT SECURITY": ["d", 1.0, 822],
    "BANK CHARGE": ["d", 1.0, 817],
    "EXPORT SERVICE FEE": ["o", 1.0, 802],
    "EQUIPMENT HANDLING": ["o", 0.96, 718],
    "SEAL FEE": ["o", 1.0, 705],
    "CMR": ["d", 0.88, 662],
    "AGENT HANDLING": ["d", 1.0, 564],
    "PORT LICENSE": ["f", 0.63, 545],
    "NEW BUNKER FACTOR": ["f", 1.0, 537],
    "INTERNATIONAL SHIP AND PORT SECURITY": ["o", 0.99, 529],
    "WAR RISK PREMIUM": ["f", 1.0, 495],
    "DELIVERY ORDER": ["f", 0.78, 446],
    "VALUE PROTECT STARTER VAS": ["f", 1.0, 410],
    "LOW SULPHUR": ["f", 1.0, 399],
    "FUEL ADJUSTMENT FACTOR": ["f", 0.98, 371],
    "FUEL COST RECOVERY": ["f", 1.0, 307],
    "EQUIPMENT TRANSFER": ["o", 1.0, 298],
    "IMPORT SERVICE FEE": ["d", 1.0, 289],
    "VALUE PROTECT COOL STANDARD": ["f", 1.0, 288],
    "CONTAINER IMBALANCE": ["f", 0.96, 263],
    "PORT ADDITIONALS": ["o", 0.86, 254],
    "ISC2": ["f", 1.0, 234],
    "EUROPE ENVIRONMENT": ["f", 1.0, 215],
    "BUNKER ADJUSTMENT FACTOR": ["f", 1.0, 212],
    "ENERGY TRANSITION": ["f", 1.0, 199],
    "T 1": ["f", 1.0, 186],
    "OVERWEIGHT FREIGHT ADDITIONAL": ["f", 1.0, 181],
    "FREETIME EXTENSION CONTRACTS": ["o", 1.0, 171],
    "CARBON REVIEW": ["f", 1.0, 144],
    "CONTAINER INSPECTION": ["d", 1.0, 141],
    "GLOBAL FUEL": ["f", 0.94, 139],
    "ADVANCE COMMERCIAL INFORMATION": ["f", 1.0, 130],
    "RECEIVING CHARGE": ["o", 1.0, 120],
    "EMERGENCY BAF": ["f", 1.0, 115],
    "EMERGENCY RISK": ["f", 0.94, 108],
    "WAR RISK": ["f", 1.0, 106],
    "TRANSHIPMENT DOC": ["o", 1.0, 97],
    "LOGISTIC FEE": ["d", 0.99, 90],
    "PANAMA CANAL TRANSIT": ["f", 1.0, 87],
    "PORT ADDITIONALS EXPORT": ["o", 1.0, 86],
    "CARBON LIMITATION": ["f", 1.0, 72],
    "FUEL AND ENERGY": ["o", 1.0, 72],
    "TAX PORT FACILITIES T3": ["d", 0.77, 71],
    "ONE BUNKER": ["f", 1.0, 71],
    "CARRIER SECURITY": ["f", 0.84, 69],
    "GREEN FUEL": ["f", 1.0, 66],
    "ADVANCED MANIFEST REGISTRATION": ["o", 1.0, 64],
    "CARGO DECLARATION": ["f", 1.0, 56],
    "POLLUTION SURCHARGE": ["o", 1.0, 48],
    "NON STACKABLE": ["o", 1.0, 48],
    "AFR AMENDMENT": ["f", 1.0, 47],
    "ADDITIONAL FREIGHT": ["f", 1.0, 46],
    "CUSTOMS MANIFEST DOCUMENTATION": ["d", 1.0, 45],
    "TERMINAL HANDLING IMO": ["o", 1.0, 32],
    "CAPTAIN PETER": ["f", 1.0, 30],
    "WAIVER": ["f", 1.0, 29],
    "INLAND HAULAGE": ["f", 1.0, 27],
    "AGENCY DOCUMENTATION": ["d", 1.0, 25],
    "FEEDER ADDITIONAL": ["d", 1.0, 24],
    "CONTAINER CLEANING FEE": ["d", 1.0, 22],
    "LOW SULPHUR ADJUSTMENT": ["f", 1.0, 22],
    "EQUIPMENT SHORTAGE": ["f", 1.0, 21],
    "CARBON TAX": ["d", 0.68, 19],
    "CONGESTION": ["o", 0.89, 19],
    "SERVICE CONTAINER": ["d", 1.0, 18],
    "SECURITY": ["d", 0.56, 16],
    "PROFIT": ["f", 1.0, 16],
    "QUARANTINE BARRIER FEE": ["o", 1.0, 16],
    "EU INNOVATION": ["f", 1.0, 14],
    "SOLAS FEE": ["o", 1.0, 14],
    "EQUIPMENT MAINTENANCE": ["d", 1.0, 13],
    "MARINE FUEL RECOVERY": ["f", 0.92, 13],
    "SEAL HANDLING": ["o", 1.0, 12],
    "LOW WATER": ["f", 0.92, 12],
    "ROAD BOND": ["o", 1.0, 12],
    "CONTAINER RELEASE": ["d", 1.0, 12],
    "UN STUFFING": ["o", 1.0, 12],
    "CUSTOMS CLEARANCE": ["o", 1.0, 11],
    "ADMINISTRATION FEE": ["d", 0.9, 10],
    "ON CARRIAGE CONGESTION": ["f", 0.6, 10],
    "CONTAINER CLEANING": ["d", 1.0, 10],
    "INLAND FUEL": ["d", 1.0, 9],
    "SECURITY MANIFEST DOCUMENT": ["f", 1.0, 9],
    "EMERGENCY PORT CONGESTION": ["d", 0.78, 9],
    "LIFT ON LIFT OFF": ["d", 1.0, 9],
    "CONTAINER FREIGHT STATION": ["o", 1.0, 8],
    "CONTAINER MAINTENANCE": ["d", 1.0, 8],
    "LOCAL TERMINAL RECOVERY": ["o", 1.0, 7],
    "COST RECOVERY": ["f", 1.0, 6],
    "FLEXITANK": ["f", 1.0, 6],
    "CONTAINER DEPOSIT": ["d", 1.0, 6],
    "EMISSION CONTROL AREAS": ["f", 1.0, 6],
    "PORT AND OR TERMINAL ACCESS": ["o", 0.67, 6],
    "CUSTOMS CHARGE UNION": ["d", 1.0, 6],
    "COLL FEE": ["d", 1.0, 6],
    "INLAND": ["d", 1.0, 5],
    "CONTAINER MANAGEMENT": ["d", 1.0, 5],
    "LOCAL OTHER FEES": ["o", 1.0, 5],
    "HAULAGE FEE": ["o", 0.8, 5],
    "ASSESSMENT LOCAL MARITIME": ["f", 1.0, 5],
    "IMPORT PACK": ["d", 1.0, 5],
    "IOF": ["o", 1.0, 5],
    "INLAND ADDITIONAL SERVICE": ["o", 0.5, 4],
    "COMMUNICATION MANAGEMENT": ["d", 1.0, 4],
    "CARGO FACILITY CHARGE": ["f", 0.75, 4],
    "CONTAINER PROTECTION": ["d", 1.0, 4],
    "CUSTOMS FINE": ["o", 1.0, 4],
    "SECURITY COMPLIANCE MANAGEMENT": ["f", 1.0, 4],
    "EMPTY DROP OFF": ["d", 1.0, 4],
    "EXPORT PACK": ["o", 1.0, 4],
    "WINE SHIPMENT": ["o", 1.0, 4],
    "PORT INFRASTRUCTURE": ["o", 0.67, 3],
    "LOCAL CHARGE": ["d", 0.67, 3],
    "OPERATION COST RECOVERY": ["d", 0.67, 3],
    "HBL FEE": ["o", 1.0, 3],
    "SERENITY CONTAINER GUARANTEE": ["o", 1.0, 3],
    "CHASSIS FEE": ["d", 0.67, 3],};

// Placed by hand: normalised charge name → section. Names are matched with and without a code in
// brackets at the end ("Fiji Wharfage (UWD)").
const MONADA = {
    // Swire Shipping, Australia → Pacific islands (quotation SRR00023922, 2026-10): the file's own
    // glossary gives each charge's side.
    "EMERGENCY BUNKER SURCHARGE": "f",
    "CARRIER SECURITY FEE": "f",
    "SPECIAL EQUIPMENT SURCHARGE": "f",
    "ORIGIN PORT SERVICE CHARGE": "o",
    "ORIGIN DOC FEE": "o",
    "EQUIPMENT HANDLING FEE ORIGIN": "o",
    "QUARANTINE BARRIER FEE": "o",
    "COLLECTION FEE DESTINATION": "d",
    "TERMINAL HANDLING CHARGE DESTINATION": "d",
    "DESTINATION PORT SERVICE CHARGE": "d",
    "FIJI ADMINISTRATION FEE": "d",
    "SAMOA CUSTOMS CHARGE DESTINATION": "d",
    "AGENCY DEST DOC FEE": "d",
    "SAMOA HANDLING IMPORT": "d",
    "FIJI WHARFAGE": "d",
    "LIFT OFF AGT": "d",
    "LIFT OFF AGENT": "d",
    "WHARFAGE AND HANDLING": "d",
    "EQUIPMENT HANDLING FEE DESTINATION": "d",
    "BIO SECURITY FEE": "d",
    "BIOSECURITY FEE": "d",
    "BIO SECURITY FACILITATION": "d",
};

const SECTION_TITLE = { o: 'Origin', f: 'Freight', d: 'Destination' };

export const normalise = title => String(title || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
// The name without the carrier's charge code, at the end in brackets or at the start before a
// dash: "Fiji Wharfage (UWD)", "UWD - Fiji Wharfage" → "FIJI WHARFAGE".
export const chargeName = title => normalise(String(title || '').replace(/\s*\([^()]*\)\s*$/, '').replace(/^\s*[A-Z0-9]{2,5}\s+[-–:]\s+/, '')) || normalise(title);

const ORIGIN_WORDS = /\b(ORIGIN|ORIG|EXPORT|EXP|POL|OTHC|THCO|PICK ?UP|COLLECTION|LOADING|STUFFING)\b/;
const DESTINATION_WORDS = /\b(DESTINATION|DEST|IMPORT|IMP|POD|DTHC|THCD|DELIVERY ORDER|D O|DELIVERY|UNLOADING|DEVANNING|STRIPPING|DEMURRAGE|DETENTION)\b/;
const CARRIER_FREIGHT = /\b(TRANSPORT ADDITIONAL|PRE ?CARRIAGE|PRECARRIAGE|ON ?CARRIAGE|ONCARRIAGE|HAULAGE|FEEDER|VESSEL SECURITY|CARRIER SECURITY)\b/;
const ORIGIN_KIND = /\b(TERMINAL|THC|SEAL|SEALING|VGM|VERIFIED GROSS MASS|ISPS|BL FEE|B L|BILL OF LADING|DOCUMENTATION|DOC FEE|EXPORT DECLARATION|CUSTOMS)\b/;
const FREIGHT_KIND = /\b(FREIGHT|BASIC|BAS|OCEAN|BUNKER|BAF|EBAF|EBS|FUEL|FAF|LSS|LOW SUL(F|PH)UR|ETS|EMISSION|ENERGY|CARBON|ENVIRONMENT|GRI|PEAK|PSS|WAR|RISK|MANIFEST|AMS|ENS|ACI|AFR|DANGEROUS|IMO|HAZ|FEEDER|TRANSPORT ADDITIONAL|PRE ?CARRIAGE|PRECARRIAGE|ON ?CARRIAGE|ONCARRIAGE|HAULAGE|VESSEL SECURITY|CARRIER SECURITY|CANAL|CONTINGENCY|EQUIPMENT IMBALANCE|CONTAINER IMBALANCE)\b/;

export const GLOSSARY = CARGOFIVE;
export { SECTION_TITLE, MONADA };

// The glossary's section for a charge, or null when the glossary doesn't know it.
export function glossarySection(title) {
    const full = normalise(title);
    const name = chargeName(title);
    if (MONADA[full] || MONADA[name]) return MONADA[full] || MONADA[name];
    const known = CARGOFIVE[full] || CARGOFIVE[name];
    if (known && known[1] >= 0.9) return known[0];
    // The carrier's own transport and security charges are freight even when they name a side
    // ("Transport Additional Origin", "Pre Carriage Haulage", "Vessel Security"), as CargoFive
    // files them.
    if (CARRIER_FREIGHT.test(full)) return 'f';
    if (ORIGIN_WORDS.test(full) && !DESTINATION_WORDS.test(full)) return 'o';
    if (DESTINATION_WORDS.test(full) && !ORIGIN_WORDS.test(full)) return 'd';
    if (known) return known[0];
    if (FREIGHT_KIND.test(full)) return 'f';
    if (ORIGIN_KIND.test(full)) return 'o';
    return null;
}

/**
 * The section key for one charge title: 'o', 'f' or 'd'. `stated` holds the sides the rate file
 * itself gives its charges (a glossary sheet's "Origin Locals"): the carrier's own word wins over
 * the glossary. `learned` holds the sections found for charges the glossary doesn't know. Both are
 * chargeName → key, from rateAgent/chargeSections.js.
 */
export function chargeSection(title, learned = null, stated = null) {
    const name = chargeName(title);
    const full = normalise(title);
    return stated?.[full] || stated?.[name] || glossarySection(title) || learned?.[name] || learned?.[full] || 'f';
}

export const SECTION_ORDER = ['Origin', 'Freight', 'Destination'];

// Whether a form already has its charges in glossary sections (and so must be kept as it is: a
// reviewer may have moved a charge).
export function isSectioned(form) {
    const titles = (form?.sections || []).map(s => s.title);
    return titles.length > 0 && titles.every(t => SECTION_ORDER.includes(t));
}

// Every field of a form, in section order.
export const formFields = form => (form?.sections || []).flatMap(s => s.fields || []);

// Move one field to another glossary section (made if missing), keeping the section order.
export function moveField(form, fieldId, toTitle) {
    const field = formFields(form).find(f => f.id === fieldId);
    if (!field) return form;
    const defaultCurrency = form.sections?.[0]?.defaultCurrency;
    const sections = form.sections.map(s => ({ ...s, fields: (s.fields || []).filter(f => f.id !== fieldId) }));
    if (!sections.some(s => s.title === toTitle)) sections.push({ title: toTitle, ...(defaultCurrency ? { defaultCurrency } : {}), fields: [] });
    const target = sections.find(s => s.title === toTitle);
    target.fields = [...target.fields, field];
    return { ...form, sections: _sortSections(sections.filter(s => s.fields.length)) };
}
const _sortSections = sections => [...sections].sort((a, b) => SECTION_ORDER.indexOf(a.title) - SECTION_ORDER.indexOf(b.title));

/**
 * A rate form with its charges in Origin, Freight and Destination sections (empty ones left out),
 * in that order. Text fields go at the top of Freight. The form's other properties are kept, and
 * so is the first section's default currency.
 */
export function sectionedForm(form, learned = null, stated = null) {
    const fields = (form?.sections || []).flatMap(s => s.fields || []);
    const defaultCurrency = form?.sections?.[0]?.defaultCurrency;
    const groups = { o: [], f: [], d: [] };
    fields.filter(f => f.type === 'string').forEach(f => groups.f.push(f));
    fields.filter(f => f.type !== 'string').forEach(f => groups[chargeSection(f.title, learned, stated)].push(f));
    const sections = ['o', 'f', 'd']
        .filter(k => groups[k].length)
        .map(k => ({ title: SECTION_TITLE[k], ...(defaultCurrency ? { defaultCurrency } : {}), fields: groups[k] }));
    return { ...form, sections: sections.length ? sections : form.sections };
}
